import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Users,
  Upload,
  Download,
  Edit,
  Trash2,
  ChevronUp,
  ChevronDown,
  RefreshCw,
  Scale,
  FolderOpen,
  UserCheck,
  Sparkles,
} from 'lucide-react';
import JudgeEditModal from './JudgeEditModal';

const SORT_COLUMNS = [
  { key: 'name', label: 'Hakim adı' },
  { key: 'isAvailableForAssignment', label: 'Tevziye açık' },
  { key: 'isExperienced', label: 'Deneyim' },
  { key: 'assignmentRatio', label: 'Tevzi oranı' },
  { key: 'score', label: 'Puan' },
  { key: 'dosyaSayisi', label: 'Dosya sayısı' },
];

export const JudgeManagement = () => {
  const [judges, setJudges] = useState([]);
  const [editingJudge, setEditingJudge] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const location = useLocation();
  const importInputRef = useRef(null);

  useEffect(() => {
    const loadJudgesFromStorage = () => {
      const raw = localStorage.getItem('judges');
      if (!raw) return;
      try {
        setJudges(JSON.parse(raw));
      } catch {
        /* ignore */
      }
    };

    loadJudgesFromStorage();

    window.addEventListener('judges-localstorage-updated', loadJudgesFromStorage);
    return () => window.removeEventListener('judges-localstorage-updated', loadJudgesFromStorage);
  }, [location.pathname]);

  const saveJudges = (updatedJudges) => {
    setJudges(updatedJudges);
    localStorage.setItem('judges', JSON.stringify(updatedJudges));
    window.dispatchEvent(new Event('judges-localstorage-updated'));
  };

  const addJudge = () => {
    const newJudge = {
      id: Date.now(),
      name: '',
      isAvailableForAssignment: true,
      isExperienced: false,
      assignmentRatio: '1',
      score: 0,
      dosyaSayisi: 0,
    };
    setEditingJudge(newJudge);
  };

  const updateJudge = (updatedJudge) => {
    if (judges.find((judge) => judge.id === updatedJudge.id)) {
      const updatedJudges = judges.map((judge) =>
        judge.id === updatedJudge.id ? updatedJudge : judge
      );
      saveJudges(updatedJudges);
    } else {
      saveJudges([...judges, updatedJudge]);
    }
    setEditingJudge(null);
  };

  const deleteJudge = (id, displayName) => {
    const label = displayName?.trim() ? `"${displayName.trim()}"` : 'Bu hakim';
    if (!window.confirm(`${label} silinsin mi? Bu işlem geri alınamaz.`)) return;
    saveJudges(judges.filter((judge) => judge.id !== id));
  };

  const exportJudges = () => {
    const dataStr = JSON.stringify(judges, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
    const exportFileDefaultName = 'judges.json';
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const importJudges = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const importedJudges = JSON.parse(e.target.result);
        if (!Array.isArray(importedJudges)) {
          alert('Dosya beklenen formatta değil (dizi olmalı).');
          return;
        }
        saveJudges(importedJudges);
      } catch {
        alert('Geçersiz JSON dosyası.');
      }
    };
    reader.readAsText(file);
    event.target.value = '';
  };

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const sortedJudges = useMemo(() => {
    const sortableJudges = [...judges];
    if (sortConfig.key === null) return sortableJudges;

    const numericKeys = new Set(['score', 'dosyaSayisi']);

    sortableJudges.sort((a, b) => {
      let av = a[sortConfig.key];
      let bv = b[sortConfig.key];
      if (numericKeys.has(sortConfig.key)) {
        av = Number(av) || 0;
        bv = Number(bv) || 0;
      }
      if (av < bv) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (av > bv) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
    return sortableJudges;
  }, [judges, sortConfig]);

  const stats = useMemo(() => {
    const n = judges.length;
    const available = judges.filter((j) => j.isAvailableForAssignment !== false).length;
    const totalDosya = judges.reduce((s, j) => s + (Number(j.dosyaSayisi) || 0), 0);
    return { n, available, totalDosya };
  }, [judges]);

  const resetPuanVeDosyaSayisi = () => {
    if (
      !window.confirm(
        'Tüm hakimlerin puanı ve dosya sayısı sıfırlansın mı? Bu işlem geri alınamaz.'
      )
    ) {
      return;
    }
    const updatedJudges = judges.map((judge) => ({
      ...judge,
      score: 0,
      dosyaSayisi: 0,
    }));
    saveJudges(updatedJudges);
  };

  const sortAriaSort = (key) => {
    if (sortConfig.key !== key) return 'none';
    return sortConfig.direction === 'ascending' ? 'ascending' : 'descending';
  };

  return (
    <div className="min-h-[calc(100vh-5rem)] bg-gradient-to-b from-slate-50 via-white to-slate-50/90 px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <header className="mb-8 border-b border-slate-200/80 pb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-white shadow-md shadow-indigo-500/25">
                  <Scale className="h-5 w-5" aria-hidden />
                </span>
                <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  Hakim Yönetimi
                </h1>
              </div>
              <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-600">
                Tevzi için kullanılacak hakim listesini buradan yönetirsiniz. Kayıtlar tarayıcıda saklanır;
                içe/dışa aktarma ile yedek alabilir veya başka cihazdan liste taşıyabilirsiniz.
              </p>
            </div>
          </div>
        </header>

        {judges.length > 0 && (
          <section
            className="mb-8 grid gap-3 sm:grid-cols-3"
            aria-label="Liste özeti"
          >
            <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-900/5">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-700">
                  <Users className="h-4 w-4" aria-hidden />
                </span>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Toplam hakim
                  </p>
                  <p className="text-2xl font-bold tabular-nums text-slate-900">{stats.n}</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-900/5">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                  <UserCheck className="h-4 w-4" aria-hidden />
                </span>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Tevziye açık
                  </p>
                  <p className="text-2xl font-bold tabular-nums text-slate-900">{stats.available}</p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm ring-1 ring-slate-900/5">
              <div className="flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-800">
                  <FolderOpen className="h-4 w-4" aria-hidden />
                </span>
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Toplam dosya (liste)
                  </p>
                  <p className="text-2xl font-bold tabular-nums text-slate-900">{stats.totalDosya}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        <section className="mb-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={addJudge}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <Users size={18} aria-hidden />
              Hakim ekle
            </button>
            <span className="hidden h-8 w-px bg-slate-200 sm:inline-block" aria-hidden />
            <button
              type="button"
              onClick={exportJudges}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <Download size={18} aria-hidden />
              Dışa aktar
            </button>
            <button
              type="button"
              onClick={() => importInputRef.current?.click()}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <Upload size={18} aria-hidden />
              İçe aktar
            </button>
            <input
              ref={importInputRef}
              type="file"
              className="sr-only"
              accept=".json,application/json"
              onChange={importJudges}
            />
          </div>
          <button
            type="button"
            onClick={resetPuanVeDosyaSayisi}
            disabled={judges.length === 0}
            className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-medium text-red-700 shadow-sm transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2"
          >
            <RefreshCw size={18} aria-hidden />
            Puan ve dosya sayısını sıfırla
          </button>
        </section>

        {judges.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white/80 px-6 py-16 text-center shadow-sm ring-1 ring-slate-900/5">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600">
              <Sparkles className="h-7 w-7" aria-hidden />
            </span>
            <h2 className="mt-6 text-lg font-semibold text-slate-900">Henüz hakim yok</h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
              Tevzi yapmadan önce en az bir hakim ekleyin veya daha önce dışa aktardığınız{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 text-xs text-slate-800">judges.json</code>{' '}
              dosyasını içe aktarın.
            </p>
            <button
              type="button"
              onClick={addJudge}
              className="mt-8 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-md shadow-indigo-500/25 transition hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              <Users size={18} aria-hidden />
              İlk hakimi ekle
            </button>
          </div>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-lg shadow-slate-900/5 ring-1 ring-slate-900/5">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50/95">
                  <tr>
                    <th
                      scope="col"
                      className="w-12 px-3 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
                    >
                      #
                    </th>
                    {SORT_COLUMNS.map(({ key, label }) => (
                      <th
                        key={key}
                        scope="col"
                        aria-sort={sortAriaSort(key)}
                        className="cursor-pointer px-4 py-3.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600 transition hover:bg-slate-100/80"
                        onClick={() => requestSort(key)}
                      >
                        <span className="inline-flex items-center gap-1">
                          {label}
                          {sortConfig.key === key &&
                            (sortConfig.direction === 'ascending' ? (
                              <ChevronUp className="h-4 w-4 shrink-0 text-indigo-600" aria-hidden />
                            ) : (
                              <ChevronDown className="h-4 w-4 shrink-0 text-indigo-600" aria-hidden />
                            ))}
                        </span>
                      </th>
                    ))}
                    <th
                      scope="col"
                      className="sticky right-0 z-10 bg-slate-50/95 px-4 py-3.5 text-right text-xs font-semibold uppercase tracking-wide text-slate-600 shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.15)] sm:shadow-none"
                    >
                      İşlem
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {sortedJudges.map((judge, index) => (
                    <tr
                      key={judge.id}
                      className="group transition-colors hover:bg-indigo-50/[0.35]"
                    >
                      <td className="whitespace-nowrap px-3 py-3 text-sm tabular-nums text-slate-400">
                        {index + 1}
                      </td>
                      <td className="max-w-[12rem] px-4 py-3 sm:max-w-none">
                        <button
                          type="button"
                          onClick={() => setEditingJudge(judge)}
                          className="text-left text-sm font-semibold text-slate-900 underline-offset-4 transition hover:text-indigo-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-1 rounded"
                        >
                          {judge.name?.trim() ? judge.name : (
                            <span className="italic font-normal text-slate-400">İsimsiz</span>
                          )}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                            judge.isAvailableForAssignment !== false
                              ? 'bg-emerald-50 text-emerald-800 ring-emerald-600/15'
                              : 'bg-red-50 text-red-800 ring-red-600/15'
                          }`}
                        >
                          {judge.isAvailableForAssignment !== false ? 'Açık' : 'Kapalı'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${
                            judge.isExperienced
                              ? 'bg-indigo-50 text-indigo-900 ring-indigo-600/15'
                              : 'bg-amber-50 text-amber-900 ring-amber-700/15'
                          }`}
                        >
                          {judge.isExperienced ? 'Deneyimli' : 'Yeni'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-slate-700">
                        {judge.assignmentRatio ?? '—'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium tabular-nums text-slate-900">
                        {judge.score ?? 0}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-slate-700">
                        {judge.dosyaSayisi ?? 0}
                      </td>
                      <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-2 py-2 text-right shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.12)] transition-colors group-hover:bg-indigo-50/[0.35] sm:shadow-none">
                        <div className="inline-flex items-center gap-0.5">
                          <button
                            type="button"
                            onClick={() => setEditingJudge(judge)}
                            className="rounded-lg p-2 text-slate-500 transition hover:bg-slate-100 hover:text-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                            title="Düzenle"
                            aria-label={`${judge.name || 'Hakim'} düzenle`}
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteJudge(judge.id, judge.name)}
                            className="rounded-lg p-2 text-slate-500 transition hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400"
                            title="Sil"
                            aria-label={`${judge.name || 'Hakim'} sil`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {editingJudge && (
          <JudgeEditModal
            judge={editingJudge}
            isNew={!judges.find((j) => j.id === editingJudge.id)}
            onClose={() => setEditingJudge(null)}
            onSave={updateJudge}
          />
        )}
      </div>
    </div>
  );
};
